import { useEffect } from 'react';
import { Play, RotateCcw, Trophy, Undo2 } from 'lucide-react';
import { GameState } from '../types/darts';
import { useT } from '../hooks/useT';
import { Modal } from './Modal';

interface LegWinModalProps {
  gameState: GameState;
  onNextLeg: () => void;
  onUndo: () => void;
  onNewGame: () => void;
}

const celebrate = (big: boolean) => {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return () => {};
  let timer: number | undefined;
  // Confetti wordt pas geladen als er iets te vieren is.
  import('canvas-confetti')
    .then(({ default: confetti }) => {
      confetti({ particleCount: big ? 150 : 80, spread: 100, origin: { y: 0.6 } });
      timer = window.setTimeout(() => {
        confetti({ particleCount: 80, angle: 60, spread: 70, origin: { x: 0.15, y: 0.7 } });
        confetti({ particleCount: 80, angle: 120, spread: 70, origin: { x: 0.85, y: 0.7 } });
      }, 250);
    })
    .catch(() => {});
  return () => window.clearTimeout(timer);
};

/** Verschijnt zodra een leg gewonnen is: volgende leg, nieuwe partij, of de laatste worp herstellen. */
export function LegWinModal({ gameState, onNextLeg, onUndo, onNewGame }: LegWinModalProps) {
  const { t, tf } = useT();
  const { config, players, isMatchOver } = gameState;
  const winner = players.find(p => p.id === gameState.legWinnerId);
  const others = players.filter(p => p.id !== winner?.id);
  const winnerLegs = winner ? gameState.legsWon[winner.id] : 0;

  useEffect(() => celebrate(isMatchOver), [isMatchOver]);

  const scoreLabel = (playerId: string) => {
    if (config.gameMode === 'cricket') return tf('cricketPoints', { n: gameState.cricketState[playerId]?.points ?? 0 });
    if (config.gameMode === 'countup') return tf('pointsUnit', { n: gameState.scores[playerId] ?? 0 });
    return tf('scoreLeft', { n: gameState.scores[playerId] ?? 0 });
  };

  return (
    <Modal title={isMatchOver ? t('matchWon') : t('legWon')} onClose={onUndo} dismissible={false}>
      <div className="space-y-4 text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-emerald-700 text-white">
          <Trophy size={32} aria-hidden />
        </div>
        <div>
          <p className="text-2xl font-bold">{winner?.name}</p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {isMatchOver ? t('matchWonText') : tf('legWonText', { won: winnerLegs, total: config.firstToLegs })}
          </p>
        </div>

        {others.length > 0 && (
          <div className="space-y-1 rounded-2xl bg-black/5 p-3 text-left dark:bg-white/5">
            <p className="text-xs font-semibold text-slate-600 uppercase dark:text-slate-400">{t('otherScores')}</p>
            <ul className="divide-y divide-black/5 dark:divide-white/10">
              {others.map(p => {
                const legs = gameState.legsWon[p.id] || 0;
                return (
                  <li key={p.id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                    <span className="truncate font-semibold">{p.name}</span>
                    <span className="flex shrink-0 items-center gap-2 font-mono">
                      <span className="font-bold">{scoreLabel(p.id)}</span>
                      {config.firstToLegs > 1 && (
                        <span className="rounded-full bg-black/5 px-2 py-0.5 font-sans text-xs dark:bg-white/10">
                          {legs === 1 ? t('legsCountOne') : tf('legsCountMany', { n: legs })}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="space-y-2">
          <button
            onClick={isMatchOver ? onNewGame : onNextLeg}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-700 font-bold whitespace-nowrap text-white transition-all duration-150 hover:bg-emerald-800 active:scale-95"
          >
            {isMatchOver ? <RotateCcw size={18} aria-hidden /> : <Play size={18} aria-hidden />}
            {isMatchOver ? t('newMatch') : t('nextLeg')}
          </button>
          <button
            onClick={onUndo}
            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all duration-150 hover:bg-black/5 active:scale-95 dark:hover:bg-white/5"
          >
            <Undo2 size={16} aria-hidden />
            {t('undoWinningDart')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
